import { useEffect, useRef, useState, type DragEvent, type KeyboardEvent, type PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { colorFor } from '@os/shared';
import { useApi } from '../../app/providers';
import { ApiError, type Profile } from '../../lib/api';
import { avatarPhoto } from '../../lib/avatar';
import { Avatar, Button, Dialog, FileButton, Slider } from '../../ui';
import { focusRing } from '../../ui/focus';
import { qk } from '../dashboard/queries';
import { imageDimensions, MAX_PIXELS, sniffImage } from '../media/import';
import { cropRect, cropToAvatar, MAX_ZOOM, type Crop } from './crop';

/** Largest file offered to the crop (a phone photo is 2-4 MB; what is sent is a ~20 KB WebP). */
export const AVATAR_MAX_FILE = 5 * 1024 * 1024;
/** Side of the framing area, in CSS pixels (fits a 375 px phone with the dialog's margins). */
const FRAME = 240;
/** How far an arrow key moves the photo, in framing pixels. */
const KEY_STEP = 8;

type Chosen = { bitmap: ImageBitmap; url: string };
type Message = { ok: boolean; text: string } | null;

/** A crop moved to where it lands once pulled back inside the image. */
function settle(w: number, h: number, c: Crop): Crop {
  const r = cropRect(w, h, c);
  return { x: (r.sx + r.size / 2) / w, y: (r.sy + r.size / 2) / h, zoom: Math.min(MAX_ZOOM, Math.max(1, Number.isFinite(c.zoom) ? c.zoom : 1)) };
}

/**
 * My photo in Settings › Profile (P5g, backlog §16 P5g.6): my disc, "Add/Change photo" (or a file dropped on the
 * disc), a dialog to frame it (drag or arrow keys to move, slider or pinch to zoom), "Remove photo". Checked before
 * decoding (5 MB, the format by its bytes, 100 Mpx); what leaves is a 256 px WebP, through the Worker.
 */
export function AvatarPicker({ profile }: { profile: Profile }) {
  const { t } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  const [chosen, setChosen] = useState<Chosen | null>(null);
  const [message, setMessage] = useState<Message>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const photo = avatarPhoto(profile.avatarKey, profile.id);

  const close = () => {
    if (chosen) {
      chosen.bitmap.close();
      URL.revokeObjectURL(chosen.url);
    }
    setChosen(null);
    setError(null);
  };

  const pick = async (file: File | undefined) => {
    if (!file) return;
    setMessage(null);
    if (file.size > AVATAR_MAX_FILE) return setMessage({ ok: false, text: t('settings.photo.tooLarge') });
    const head = new Uint8Array(await file.slice(0, 64 * 1024).arrayBuffer());
    const kind = sniffImage(head);
    if (!kind) return setMessage({ ok: false, text: t('settings.photo.notImage') });
    const dims = imageDimensions(head, kind);
    if (dims && dims.w * dims.h > MAX_PIXELS) return setMessage({ ok: false, text: t('settings.photo.unreadable') });
    try {
      const bitmap = await createImageBitmap(file);
      setChosen({ bitmap, url: URL.createObjectURL(file) });
    } catch {
      setMessage({ ok: false, text: t('settings.photo.unreadable') });
    }
  };

  const save = async (crop: Crop) => {
    if (!chosen) return;
    setBusy(true);
    setError(null);
    try {
      await api.setAvatar(await cropToAvatar(chosen.bitmap, crop));
      await queryClient.invalidateQueries({ queryKey: qk.profile });
      close();
      setMessage({ ok: true, text: t('settings.photo.saved') });
    } catch (e) {
      setError(e instanceof ApiError && e.code === 'limit' ? t('settings.photo.limit') : t('auth.genericError'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setMessage(null);
    try {
      await api.removeAvatar();
      await queryClient.invalidateQueries({ queryKey: qk.profile });
      setMessage({ ok: true, text: t('settings.photo.removed') });
    } catch {
      setMessage({ ok: false, text: t('auth.genericError') });
    } finally {
      setBusy(false);
    }
  };

  const drop = (e: DragEvent) => {
    e.preventDefault();
    void pick(e.dataTransfer.files[0]);
  };

  return (
    <div className="flex flex-col gap-2">
      <span className="font-sans text-sm font-medium text-fg">{t('settings.photo.label')}</span>
      <div className="flex items-center gap-4">
        <div data-avatar-drop="" onDragOver={(e) => e.preventDefault()} onDrop={drop} className="rounded-full">
          <Avatar name={profile.displayName || '—'} color={profile.color ?? colorFor(profile.id)} photo={photo} size="xl" />
        </div>
        <div className="flex flex-wrap gap-2">
          <FileButton accept="image/jpeg,image/png,image/webp,image/gif,image/avif,image/heic" onFiles={(files) => void pick(files[0])} disabled={busy}>
            {photo ? t('settings.photo.change') : t('settings.photo.add')}
          </FileButton>
          {photo && (
            <Button variant="ghost" disabled={busy} onClick={() => void remove()}>
              {t('settings.photo.remove')}
            </Button>
          )}
        </div>
      </div>
      <span className="font-sans text-xs text-fg-muted">{t('settings.photo.hint')}</span>
      {message && (
        <p role={message.ok ? 'status' : 'alert'} className={message.ok ? 'font-sans text-sm text-fg-muted' : 'font-sans text-xs text-danger'}>
          {message.text}
        </p>
      )}
      {chosen && <CropDialog chosen={chosen} busy={busy} error={error} onCancel={close} onSave={(c) => void save(c)} />}
    </div>
  );
}

function CropDialog({ chosen, busy, error, onCancel, onSave }: { chosen: Chosen; busy: boolean; error: string | null; onCancel: () => void; onSave: (c: Crop) => void }) {
  const { t } = useTranslation();
  const { width: w, height: h } = chosen.bitmap;
  const [crop, setCrop] = useState<Crop>({ x: 0.5, y: 0.5, zoom: 1 });
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const r = cropRect(w, h, crop);
  const scale = FRAME / r.size;

  /** Moves the photo by a drag of (dx, dy) framing pixels. */
  const pan = (dx: number, dy: number) =>
    setCrop((c) => {
      const s = FRAME / cropRect(w, h, c).size;
      return settle(w, h, { ...c, x: c.x - dx / s / w, y: c.y - dy / s / h });
    });
  const zoomTo = (zoom: number) => setCrop((c) => settle(w, h, { ...c, zoom }));

  const down = (e: PointerEvent) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
  };
  const move = (e: PointerEvent) => {
    const all = pointers.current;
    const last = all.get(e.pointerId);
    if (!last) return;
    const others = [...all.entries()].filter(([id]) => id !== e.pointerId).map(([, p]) => p);
    if (others.length === 0) pan(e.clientX - last.x, e.clientY - last.y);
    else {
      // Pinch: the zoom follows the distance between the two fingers.
      const o = others[0]!;
      const before = Math.hypot(last.x - o.x, last.y - o.y);
      const after = Math.hypot(e.clientX - o.x, e.clientY - o.y);
      if (before > 0) setCrop((c) => settle(w, h, { ...c, zoom: c.zoom * (after / before) }));
    }
    all.set(e.pointerId, { x: e.clientX, y: e.clientY });
  };
  const up = (e: PointerEvent) => pointers.current.delete(e.pointerId);

  const key = (e: KeyboardEvent) => {
    const step = { ArrowLeft: [KEY_STEP, 0], ArrowRight: [-KEY_STEP, 0], ArrowUp: [0, KEY_STEP], ArrowDown: [0, -KEY_STEP] }[e.key];
    if (step) {
      e.preventDefault();
      pan(step[0]!, step[1]!);
    } else if (e.key === '+' || e.key === '=') zoomTo(crop.zoom + 0.1);
    else if (e.key === '-') zoomTo(crop.zoom - 0.1);
  };

  // A wheel over the framing zooms it, without scrolling the dialog.
  const area = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = area.current;
    if (!el) return;
    const wheel = (e: WheelEvent) => {
      e.preventDefault();
      setCrop((c) => settle(w, h, { ...c, zoom: c.zoom * Math.exp(-e.deltaY / 400) }));
    };
    el.addEventListener('wheel', wheel, { passive: false });
    return () => el.removeEventListener('wheel', wheel);
  }, [w, h]);

  return (
    <Dialog
      title={t('settings.photo.cropTitle')}
      description={t('settings.photo.cropHint')}
      onClose={onCancel}
      footer={
        <>
          <Button onClick={onCancel}>{t('settings.photo.cancel')}</Button>
          <Button variant="primary" disabled={busy} onClick={() => onSave(crop)}>
            {busy ? t('settings.photo.saving') : t('settings.photo.save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col items-center gap-4">
        <div
          ref={area}
          role="group"
          tabIndex={0}
          aria-label={t('settings.photo.area')}
          data-crop-area=""
          onPointerDown={down}
          onPointerMove={move}
          onPointerUp={up}
          onPointerCancel={up}
          onKeyDown={key}
          className={`relative cursor-grab touch-none overflow-hidden rounded-xl bg-surface-2 select-none active:cursor-grabbing ${focusRing}`}
          style={{ width: FRAME, height: FRAME }}
        >
          <img
            src={chosen.url}
            alt=""
            draggable={false}
            className="pointer-events-none absolute max-w-none"
            style={{ width: w * scale, height: h * scale, left: -r.sx * scale, top: -r.sy * scale }}
          />
          {/* The round frame: what the disc will show. */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-full shadow-[0_0_0_9999px_var(--color-overlay)] ring-2 ring-surface" />
        </div>
        <Slider label={t('settings.photo.zoom')} value={crop.zoom} min={1} max={MAX_ZOOM} step={0.01} onValueChange={zoomTo} format={(v) => `${Math.round(v * 100)} %`} className="w-full" />
        {error && (
          <p role="alert" className="self-start font-sans text-xs text-danger">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}
